import React, { useEffect, useState } from 'react';

import { cssMap } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import type { EmojiProvider } from '@atlaskit/emoji/resource';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { Reactions, ReactionStatus } from '@atlaskit/reactions';
import { token } from '@atlaskit/tokens';
import { getCloudUrlFromId } from '@atlassian/conversation-assistant-agent/services/cloud-url';

import type { PublishedConfluenceData } from '../../../../common/types';

import { messages } from './messages';

const styles = cssMap({
	avatar: {
		paddingRight: token('space.200'),
	},
});

export const ConfluenceDetails = ({
	publishedContentData,
	emojiProvider,
	cloudId,
}: {
	publishedContentData: PublishedConfluenceData;
	emojiProvider?: Promise<EmojiProvider>;
	cloudId: string;
}): React.JSX.Element => {
	const { contributors, reactions, lastUpdated } = publishedContentData;
	const [cloudUrl, setCloudUrl] = useState('');

	const normalizedReactions =
		reactions?.reactionsSummaryForEmoji?.map((reaction) => ({
			emojiId: reaction.emojiId,
			count: reaction.count,
			ari: reactions.ari,
			containerAri: reactions.containerAri,
			reacted: reaction.reacted,
		})) || [];

	const lastUpdatedText = lastUpdated === 'A moment ago' ? 'a moment ago' : lastUpdated;

	useEffect(() => {
		const getCloudUrl = async () => {
			try {
				const data = await getCloudUrlFromId(cloudId);
				setCloudUrl(data?.cloudUrl ?? '');
			} catch (error) {}
		};
		getCloudUrl();
	}, [cloudId]);

	const contributorsSrc = `${cloudUrl}${contributors?.[0]?.profilePicture?.path}`;

	return (
		<>
			<Text color="color.text.subtlest" size="small" maxLines={3}>
				{publishedContentData?.excerpt?.trim() ?? ''}
			</Text>
			<Inline alignBlock="center">
				<Box xcss={styles.avatar}>
					<Avatar appearance="circle" size="xxsmall" src={contributorsSrc} />
				</Box>
				{emojiProvider && (
					<Reactions
						reactions={normalizedReactions}
						status={ReactionStatus.ready}
						loadReaction={() => {}}
						onSelection={() => {}}
						onReactionClick={() => {}}
						emojiProvider={emojiProvider}
						allowAllEmojis
						quickReactionEmojis={undefined}
						summaryViewEnabled
						summaryViewPlacement="bottom-start"
						subtleReactionsSummaryAndPicker
						isViewOnly
					/>
				)}
				{lastUpdated && (
					<Text size="small" color="color.text.subtle">
						<FormattedMessage {...messages.updatedText} values={{ timeAgo: lastUpdatedText }} />
					</Text>
				)}
			</Inline>
		</>
	);
};
